// Post-login redirect single-slot cache. Keeps parity with pendingLinkToken
// but for arbitrary console-scoped paths (typed by the user, followed from an
// old bookmark, etc.) so that after sign-in we land where the user actually
// wanted, not the default dashboard.
//
// Only ever holds ONE pending path — if a user types a second URL after
// stashing the first, the second wins. Consumers must call consume() exactly
// once per sign-in, and reject unsafe targets (schemes, hosts, protocol
// switches) even if a caller ignores the shouldStashForPostLogin gate.

const PENDING_REDIRECT_KEY = 'timelining.pendingRedirectPath'

/**
 * Only allow same-origin absolute paths — no schemes, no hosts, no
 * protocol-relative URLs. Anything else is treated as a hostile input and
 * silently dropped so a poisoned localStorage cell can't redirect the user
 * off-domain after they sign in.
 *
 * Rejects:
 *   - non-string values (including nulls)
 *   - empty strings and anything longer than 2048 chars
 *   - anything not starting with '/'
 *   - protocol-relative '//host/path'
 *   - backslashes (Windows-style paths some browsers normalize into '\\')
 *   - any whitespace character (space/tab/newline/etc) — browsers strip these
 *     from real URLs, so a survivor here means the value was hand-crafted
 */
export const isSafeRedirectPath = (candidate: unknown): candidate is string => {
  if (typeof candidate !== 'string') return false
  if (candidate.length === 0 || candidate.length > 2048) return false
  if (!candidate.startsWith('/')) return false
  if (candidate.startsWith('//')) return false
  if (candidate.includes('\\')) return false
  if (/\s/.test(candidate)) return false
  return true
}

export const stashPendingRedirect = (path: string): void => {
  if (!isSafeRedirectPath(path)) return
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(PENDING_REDIRECT_KEY, path)
  } catch {
    // localStorage unavailable (private mode, quota) — silent fallback; the
    // user just lands on the default post-login surface instead.
  }
}

export const peekPendingRedirect = (): string | null => {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(PENDING_REDIRECT_KEY)
    return isSafeRedirectPath(raw) ? raw : null
  } catch {
    return null
  }
}

export const consumePendingRedirect = (): string | null => {
  const value = peekPendingRedirect()
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.removeItem(PENDING_REDIRECT_KEY)
    } catch {
      // Ignore — the peek already returned the value we care about.
    }
  }
  return value
}

export const clearPendingRedirect = (): void => {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.removeItem(PENDING_REDIRECT_KEY)
  } catch {
    // Ignore.
  }
}
